import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';

// Helper function to get status name
const getStatusName = (status) => {
return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to format date
const formatDate = (dateString) => {
return new Date(dateString).toLocaleDateString();
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
const words = text.split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Helper function to get branch name from branch ID
const getBranchName = (branchId, branchData) => {
if (!branchData || !branchId) return 'N/A';
const branch = branchData.find(b => b.id == branchId);
return branch ? `${branch.name} (${branch.code})` : 'N/A';
};

// Client-side Excel export for Facility Types - Show all attributes
export const exportFacilityTypesToExcel = (facilityTypes, branchData = []) => {
try {
    // Prepare data for Excel with all attributes
    const excelData = facilityTypes.map((facilityType, index) => ({
    'No.': index + 1,
    'ID': facilityType.id,
    'Name': facilityType.name,
    'Status': getStatusName(facilityType.status),
    'Description': facilityType.description,
    'Branch': getBranchName(facilityType.branch_id, branchData),
    'Created Date': formatDate(facilityType.created_at),
    'Updated Date': formatDate(facilityType.updated_at)
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
    Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
        widths[idx] = length;
        }
    });
    return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Facility Types');

    // Generate Excel file and download
    const fileName = `facility_types_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Facility Types Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Facility Types - Landscape mode, only Date Created, Name, Branch
export const exportFacilityTypesToPDF = (facilityTypes, branchData = [], filters = {}) => {
try {
    // Create new PDF document in landscape mode
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Facility Types Report',
    subject: 'Facility Types Export',
    author: companyInfo.name,
    keywords: 'facility types, export, report',
    creator: companyInfo.name
    });
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(33, 33, 33);
    doc.text('FACILITY TYPES REPORT', doc.internal.pageSize.width / 2, 20, { align: 'center' });
    
    // Add generation details
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);
    doc.text(`Total Records: ${facilityTypes.length}`, 14, 42);
    
    // Prepare table data - Only Date Created, Facility Type Name, Branch
    let currentY = 50;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const rowHeight = 10;
    
    // Column widths for landscape mode (Date Created, Name, Branch)
    const colWidths = [60, 180, 100];
    
    // Draw table header
    doc.setFillColor(240, 240, 240);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');
    
    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    // Header texts
    const headers = ['Date Created', 'Facility Type Name', 'Branch'];
    let xPos = margin;
    
    headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
    });
    
    // Draw header underline
    doc.setDrawColor(25, 118, 210);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    
    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    
    facilityTypes.forEach((facilityType, rowIndex) => {
    // Check for page break
    if (currentY + rowHeight > doc.internal.pageSize.height - 20) {
        doc.addPage('landscape');
        currentY = margin;
        
        // Redraw header on new page
        doc.setFillColor(240, 240, 240);
        doc.setTextColor(25, 118, 210);
        doc.setFont(undefined, 'bold');
        
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        xPos = margin;
        headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
        });
        
        doc.setDrawColor(25, 118, 210);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
        doc.setFillColor(248, 248, 248);
    } else {
        doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    // Get branch name
    const branchName = getBranchName(facilityType.branch_id, branchData);
    
    // Row data
    const rowData = [
        formatDate(facilityType.created_at),
        facilityType.name,
        branchName
    ];
    
    // Track maximum lines in this row
    let maxLines = 1;
    
    // Draw row text
    xPos = margin;
    rowData.forEach((cell, cellIndex) => {
        const cellText = String(cell);
        const maxWidth = colWidths[cellIndex] - 4;
        
        // Handle text wrapping for long content (especially for Name and Branch)
        let lines = [cellText];
        if (doc.getTextWidth(cellText) > maxWidth) {
        lines = splitTextIntoLines(doc, cellText, maxWidth);
        }
        
        // Update maxLines for this row
        if (lines.length > maxLines) {
        maxLines = lines.length;
        }
        
        lines.forEach((line, lineIndex) => {
        doc.text(line, xPos + 2, currentY + 7 + (lineIndex * 5));
        });
        
        xPos += colWidths[cellIndex];
    });
    
    // Adjust Y position based on the maximum number of lines in this row
    currentY += rowHeight + ((maxLines - 1) * 5);
    });
    
    const fileName = `facility_types_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Facility Types PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// Enhanced PDF export (alias for backward compatibility)
export const exportFacilityTypesToPDFEnhanced = exportFacilityTypesToPDF;

// CSV export for Facility Types - Show all attributes
export const exportFacilityTypesToCSV = (facilityTypes, branchData = []) => {
try {
    // Headers with all attributes
    const headers = ['ID', 'Name', 'Status', 'Description', 'Branch', 'Created Date', 'Updated Date'];
    
    // Prepare CSV data with all attributes
    const csvData = facilityTypes.map(facilityType => [
    facilityType.id,
    `"${facilityType.name.replace(/"/g, '""')}"`,
    getStatusName(facilityType.status),
    `"${facilityType.description.replace(/"/g, '""')}"`,
    `"${getBranchName(facilityType.branch_id, branchData).replace(/"/g, '""')}"`,
    `"${formatDate(facilityType.created_at)}"`,
    `"${formatDate(facilityType.updated_at)}"`
    ]);

    const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `facility_types_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Facility Types CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};